import React from 'react';
import * as Icons from 'lucide-react';
import SMSTrafficChart from './SMSTrafficChart';

const DynamicWidget = ({ widget, index }) => {
  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="w-8 h-8" /> : <Icons.Activity className="w-8 h-8" />;
  };

  // Determine if this is a featured widget (larger size)
  const isFeatured = (widget.width >= 2 && widget.height >= 2) || widget.priority === 'high';
  
  // Get category-based gradient colors
  const getCategoryGradient = () => {
    const colorMap = {
      '#10B981': 'from-emerald-500/30 via-green-500/20 to-teal-500/30', // Green - Financial
      '#3B82F6': 'from-blue-500/30 via-cyan-500/20 to-indigo-500/30',   // Blue - Messages
      '#8B5CF6': 'from-purple-500/30 via-violet-500/20 to-fuchsia-500/30', // Purple - Users
      '#F59E0B': 'from-amber-500/30 via-orange-500/20 to-yellow-500/30',   // Orange - Alerts
      '#06B6D4': 'from-cyan-500/30 via-sky-500/20 to-blue-500/30',      // Cyan - System
      '#EF4444': 'from-red-500/30 via-rose-500/20 to-pink-500/30',      // Red - Errors
    };
    return colorMap[widget.color] || 'from-gray-500/20 via-slate-500/15 to-gray-500/20';
  };

  const getWidgetContent = () => {
    switch (widget.widget_type || widget.widgetType) {
      case 'kpi':
      case 'stat':
        return (
          <div className={`flex ${isFeatured ? 'flex-col' : 'flex-col sm:flex-row sm:items-center sm:justify-between'} h-full`}>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className={`${isFeatured ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'} font-semibold text-gray-700 tracking-wide uppercase truncate pr-2`}>
                  {widget.title}
                </p>
                <div
                  className={`${isFeatured ? 'p-3 sm:p-4' : 'p-2 sm:p-3'} rounded-xl bg-gray-50 flex-shrink-0`}
                >
                  <div style={{ color: widget.color || '#6366f1' }}>
                    {getIcon(widget.icon || 'Activity')}
                  </div>
                </div>
              </div>
              <p className={`${isFeatured ? 'text-3xl sm:text-4xl' : 'text-xl sm:text-2xl'} font-bold text-gray-900 mb-1 sm:mb-2`}>
                {widget.value || '0'}
              </p>
              {widget.description && (
                <p className="text-xs sm:text-sm text-gray-600 font-medium mt-1 sm:mt-2">{widget.description}</p>
              )}
              {isFeatured && (
                <div className="mt-4 pt-4 border-t border-gray-200/50">
                  <div className="flex items-center gap-2 text-sm">
                    <Icons.TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-green-600 font-semibold">+12.5%</span>
                    <span className="text-gray-500">vs last period</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'chart':
        // PROMPT-050: Check if this is the SMS Traffic Chart widget
        if (widget.type === 'sms_traffic_chart' || widget.data_source === 'sms_traffic_trend') {
          return <SMSTrafficChart />;
        }
        return (
          <div>
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">{widget.title}</h3>
            <div className="flex items-center justify-center h-24 sm:h-32 text-gray-400">
              <p className="text-xs sm:text-sm">Chart: {widget.value || 'No data'}</p>
            </div>
          </div>
        );

      case 'table':
        return (
          <div>
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">{widget.title}</h3>
            <div className="text-xs sm:text-sm text-gray-600">
              <p>{widget.value || 'No data available'}</p>
            </div>
          </div>
        );

      case 'list':
        return (
          <div>
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">{widget.title}</h3>
            <div className="space-y-1 sm:space-y-2">
              <p className="text-xs sm:text-sm text-gray-600">{widget.value || 'No items'}</p>
            </div>
          </div>
        );

      default:
        return (
          <div>
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">{widget.title}</h3>
            <p className="text-base sm:text-lg font-bold text-gray-900">{widget.value ?? 0}</p>
          </div>
        );
    }
  };

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 ${isFeatured ? 'p-4 sm:p-6' : 'p-4 sm:p-5'} shadow-sm hover:shadow-md transition-shadow duration-200`}
      style={{
        gridColumn: `span ${widget.width || 1}`,
        gridRow: `span ${widget.height || 1}`,
      }}
    >
      {getWidgetContent()}
    </div>
  );
};

export default DynamicWidget;
